import * as THREE from "three";
import { makeTangentFrame, sampleTerrain, terrainHeight } from "./planet-terrain";
import type { LocalPatchConfig, PlanetDefinition, TangentFrame } from "./types";

function createMorphTerrainMaterial(water = false): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    name: water ? "planet-local-water" : "planet-local-terrain",
    transparent: water,
    depthWrite: !water,
    side: THREE.DoubleSide,
    uniforms: {
      uCurvature: { value: 0 },
      uOpacity: { value: 1 },
      uLightDirection: { value: new THREE.Vector3(0.4, 0.82, 0.35).normalize() },
      uAmbient: { value: water ? 0.42 : 0.28 },
      uWater: { value: water ? 1 : 0 },
      uTime: { value: 0 },
    },
    vertexShader: `
      uniform float uCurvature;
      uniform float uTime;
      uniform float uWater;
      attribute vec3 aCurvedPosition;
      attribute vec3 aCurvedNormal;
      varying vec3 vColor;
      varying vec3 vNormal;
      varying vec3 vWorld;
      varying float vHeight;
      void main() {
        vec3 p = mix(position, aCurvedPosition, uCurvature);
        if (uWater > 0.5) {
          float ripple = sin((p.x + p.z) * .045 + uTime * 1.4) * .32;
          ripple += sin(p.x * .083 - p.z * .057 - uTime * .9) * .19;
          p.y += ripple * (1.0 - uCurvature * .45);
        }
        vec3 n = normalize(mix(normal, aCurvedNormal, uCurvature));
        vec4 world = modelMatrix * vec4(p, 1.0);
        vColor = color;
        vNormal = normalize(mat3(modelMatrix) * n);
        vWorld = world.xyz;
        vHeight = p.y;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: `
      uniform float uOpacity;
      uniform vec3 uLightDirection;
      uniform float uAmbient;
      uniform float uWater;
      varying vec3 vColor;
      varying vec3 vNormal;
      varying vec3 vWorld;
      void main() {
        vec3 normal = normalize(gl_FrontFacing ? vNormal : -vNormal);
        float diffuse = max(dot(normal, normalize(uLightDirection)), 0.0);
        float hemi = normal.y * .5 + .5;
        vec3 lit = vColor * (uAmbient + diffuse * .72 + hemi * .11);
        float alpha = uOpacity;
        if (uWater > .5) {
          vec3 viewDir = normalize(cameraPosition - vWorld);
          float fresnel = .08 + .72 * pow(1.0 - max(dot(viewDir, normal), 0.0), 4.0);
          lit = mix(lit * vec3(.70, .92, 1.05), vec3(.19, .55, .78), fresnel);
          alpha *= .72;
        }
        gl_FragColor = vec4(lit, alpha);
      }
    `,
    vertexColors: true,
  });
}

interface PatchBuild {
  terrain: THREE.BufferGeometry;
  water: THREE.BufferGeometry;
  treePositions: THREE.Vector3[];
  treeColors: THREE.Color[];
}

export class LocalPlanetPatch {
  readonly group = new THREE.Group();
  readonly terrainMaterial = createMorphTerrainMaterial(false);
  readonly waterMaterial = createMorphTerrainMaterial(true);
  readonly terrainMesh = new THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>(new THREE.BufferGeometry(), this.terrainMaterial);
  readonly waterMesh = new THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>(new THREE.BufferGeometry(), this.waterMaterial);
  readonly treeRoot = new THREE.Group();
  readonly logicalChunkCount: number;
  frame: TangentFrame;
  planet: PlanetDefinition;
  private trunkMesh: THREE.InstancedMesh | null = null;
  private crownMesh: THREE.InstancedMesh | null = null;

  constructor(planet: PlanetDefinition, private readonly config: LocalPatchConfig, anchorNormal = new THREE.Vector3(0, 1, 0)) {
    this.planet = planet;
    this.frame = makeTangentFrame(anchorNormal, planet);
    this.logicalChunkCount = (config.chunkRadius * 2 + 1) ** 2;
    this.group.name = "local-tangent-patch";
    this.terrainMesh.name = "local-terrain";
    this.waterMesh.name = "local-water";
    this.terrainMesh.receiveShadow = true;
    this.terrainMesh.castShadow = true;
    this.waterMesh.renderOrder = 2;
    this.group.add(this.terrainMesh, this.waterMesh, this.treeRoot);
    this.rebuild();
  }

  setPlanet(planet: PlanetDefinition, anchorNormal = new THREE.Vector3(0, 1, 0)): void {
    this.planet = planet;
    this.frame = makeTangentFrame(anchorNormal, planet);
    this.rebuild();
  }

  reanchor(normal: THREE.Vector3): void {
    this.frame = makeTangentFrame(normal, this.planet);
    this.rebuild();
  }

  update(curvature: number, opacity: number, elapsed: number): void {
    this.terrainMaterial.uniforms.uCurvature!.value = curvature;
    this.terrainMaterial.uniforms.uOpacity!.value = opacity;
    this.terrainMaterial.uniforms.uTime!.value = elapsed;
    this.waterMaterial.uniforms.uCurvature!.value = curvature;
    this.waterMaterial.uniforms.uOpacity!.value = opacity;
    this.waterMaterial.uniforms.uTime!.value = elapsed;
    this.treeRoot.visible = opacity > 0.03 && curvature < 0.48;
  }

  localToPlanet(local: THREE.Vector3, target = new THREE.Vector3()): THREE.Vector3 {
    return target
      .copy(this.frame.anchorPoint)
      .addScaledVector(this.frame.east, local.x)
      .addScaledVector(this.frame.normal, local.y)
      .addScaledVector(this.frame.north, local.z);
  }

  planetToLocal(point: THREE.Vector3, target = new THREE.Vector3()): THREE.Vector3 {
    const delta = point.clone().sub(this.frame.anchorPoint);
    return target.set(delta.dot(this.frame.east), delta.dot(this.frame.normal), delta.dot(this.frame.north));
  }

  surfacePointForOffset(x: number, z: number, target = new THREE.Vector3()): THREE.Vector3 {
    const radius = this.planet.diameterMeters * 0.5;
    const direction = target
      .copy(this.frame.normal)
      .multiplyScalar(radius)
      .addScaledVector(this.frame.east, x)
      .addScaledVector(this.frame.north, z)
      .normalize();
    return direction.multiplyScalar(radius + terrainHeight(direction, this.planet));
  }

  groundHeightAtLocal(x: number, z: number): number {
    const point = this.surfacePointForOffset(x, z);
    return this.planetToLocal(point).y;
  }

  dispose(): void {
    this.terrainMesh.geometry.dispose();
    this.waterMesh.geometry.dispose();
    this.terrainMaterial.dispose();
    this.waterMaterial.dispose();
    this.disposeTrees();
  }

  private rebuild(): void {
    const built = this.buildGeometry();
    const oldTerrain = this.terrainMesh.geometry;
    const oldWater = this.waterMesh.geometry;
    this.terrainMesh.geometry = built.terrain;
    this.waterMesh.geometry = built.water;
    oldTerrain.dispose();
    oldWater.dispose();
    this.installTrees(built.treePositions, built.treeColors);
  }

  private buildGeometry(): PatchBuild {
    const patchSpan = this.config.chunkSizeMeters * (this.config.chunkRadius * 2 + 1);
    const cellSize = this.config.cellSizeMeters;
    const cells = Math.max(8, Math.round(patchSpan / cellSize));
    const half = patchSpan * 0.5;
    const row = cells + 1;
    const radius = this.planet.diameterMeters * 0.5;
    const heights = new Float32Array(row * row);
    const directions: THREE.Vector3[] = new Array(row * row);
    const samples = new Array<ReturnType<typeof sampleTerrain>>(row * row);
    const point = new THREE.Vector3();

    for (let z = 0; z <= cells; z += 1) {
      const localZ = -half + z * cellSize;
      for (let x = 0; x <= cells; x += 1) {
        const localX = -half + x * cellSize;
        const index = z * row + x;
        const direction = new THREE.Vector3()
          .copy(this.frame.normal)
          .multiplyScalar(radius)
          .addScaledVector(this.frame.east, localX)
          .addScaledVector(this.frame.north, localZ)
          .normalize();
        directions[index] = direction;
        const sample = sampleTerrain(direction, this.planet);
        samples[index] = sample;
        point.copy(direction).multiplyScalar(radius + sample.height);
        heights[index] = this.planetToLocal(point).y;
      }
    }

    const positions: number[] = [];
    const curved: number[] = [];
    const normals: number[] = [];
    const curvedNormals: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];

    for (let z = 0; z <= cells; z += 1) {
      const localZ = -half + z * cellSize;
      for (let x = 0; x <= cells; x += 1) {
        const localX = -half + x * cellSize;
        const index = z * row + x;
        const h = heights[index]!;
        positions.push(localX, h, localZ);
        const direction = directions[index]!;
        const global = direction.clone().multiplyScalar(radius + samples[index]!.height);
        const curvedLocal = this.planetToLocal(global);
        curved.push(curvedLocal.x, curvedLocal.y, curvedLocal.z);

        const left = heights[z * row + Math.max(0, x - 1)]!;
        const right = heights[z * row + Math.min(cells, x + 1)]!;
        const down = heights[Math.max(0, z - 1) * row + x]!;
        const up = heights[Math.min(cells, z + 1) * row + x]!;
        const flatNormal = new THREE.Vector3(left - right, cellSize * 2, down - up).normalize();
        normals.push(flatNormal.x, flatNormal.y, flatNormal.z);
        curvedNormals.push(direction.dot(this.frame.east), direction.dot(this.frame.normal), direction.dot(this.frame.north));
        const color = samples[index]!.color;
        colors.push(color.r, color.g, color.b);
      }
    }
    for (let z = 0; z < cells; z += 1) {
      for (let x = 0; x < cells; x += 1) {
        const a = z * row + x;
        const b = a + 1;
        const c = a + row;
        const d = c + 1;
        indices.push(a, c, b, b, c, d);
      }
    }

    const terrain = new THREE.BufferGeometry();
    terrain.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    terrain.setAttribute("aCurvedPosition", new THREE.Float32BufferAttribute(curved, 3));
    terrain.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
    terrain.setAttribute("aCurvedNormal", new THREE.Float32BufferAttribute(curvedNormals, 3));
    terrain.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    terrain.setIndex(indices);
    terrain.computeBoundingSphere();

    const waterPositions: number[] = [];
    const waterCurved: number[] = [];
    const waterNormals: number[] = [];
    const waterCurvedNormals: number[] = [];
    const waterColors: number[] = [];
    const waterIndices: number[] = [];
    const seaRadius = radius + this.planet.seaLevelMeters + 1.4;
    const waterColor = new THREE.Color(this.planet.palette.oceanShallow);

    for (let z = 0; z < cells; z += 1) {
      for (let x = 0; x < cells; x += 1) {
        const corners = [z * row + x, z * row + x + 1, (z + 1) * row + x + 1, (z + 1) * row + x];
        const average = corners.reduce((sum, index) => sum + samples[index]!.height, 0) / 4;
        if (average >= this.planet.seaLevelMeters) continue;
        const base = waterPositions.length / 3;
        const localCorners: [number, number][] = [
          [-half + x * cellSize, -half + z * cellSize],
          [-half + (x + 1) * cellSize, -half + z * cellSize],
          [-half + (x + 1) * cellSize, -half + (z + 1) * cellSize],
          [-half + x * cellSize, -half + (z + 1) * cellSize],
        ];
        for (let i = 0; i < 4; i += 1) {
          const [lx, lz] = localCorners[i]!;
          const direction = directions[corners[i]!]!;
          const curvedPoint = this.planetToLocal(direction.clone().multiplyScalar(seaRadius));
          const flatSeaPoint = this.planetToLocal(this.frame.normal.clone().multiplyScalar(radius + this.planet.seaLevelMeters + 1.4));
          waterPositions.push(lx, flatSeaPoint.y, lz);
          waterCurved.push(curvedPoint.x, curvedPoint.y, curvedPoint.z);
          waterNormals.push(0, 1, 0);
          waterCurvedNormals.push(direction.dot(this.frame.east), direction.dot(this.frame.normal), direction.dot(this.frame.north));
          waterColors.push(waterColor.r, waterColor.g, waterColor.b);
        }
        waterIndices.push(base, base + 3, base + 1, base + 1, base + 3, base + 2);
      }
    }
    const water = new THREE.BufferGeometry();
    water.setAttribute("position", new THREE.Float32BufferAttribute(waterPositions, 3));
    water.setAttribute("aCurvedPosition", new THREE.Float32BufferAttribute(waterCurved, 3));
    water.setAttribute("normal", new THREE.Float32BufferAttribute(waterNormals, 3));
    water.setAttribute("aCurvedNormal", new THREE.Float32BufferAttribute(waterCurvedNormals, 3));
    water.setAttribute("color", new THREE.Float32BufferAttribute(waterColors, 3));
    water.setIndex(waterIndices);
    water.computeBoundingSphere();

    const treePositions: THREE.Vector3[] = [];
    const treeColors: THREE.Color[] = [];
    for (let z = 2; z < cells - 2; z += 4) {
      for (let x = 2; x < cells - 2; x += 4) {
        const index = z * row + x;
        const sample = samples[index]!;
        const pseudo = Math.sin((x * 92.71 + z * 51.37 + this.planet.seed) * 0.173) * 0.5 + 0.5;
        if (sample.biome !== "lowland" || sample.moisture < 0.48 || pseudo < 0.72) continue;
        treePositions.push(new THREE.Vector3(-half + x * cellSize, heights[index]!, -half + z * cellSize));
        treeColors.push(sample.color.clone());
      }
    }
    return { terrain, water, treePositions, treeColors };
  }

  private installTrees(positions: THREE.Vector3[], colors: THREE.Color[]): void {
    this.disposeTrees();
    if (positions.length === 0) return;
    const trunkGeometry = new THREE.CylinderGeometry(0.8, 1.1, 8, 5);
    const crownGeometry = new THREE.ConeGeometry(4.6, 11, 7);
    const trunkMaterial = new THREE.MeshStandardMaterial({ color: "#6a4528", roughness: 1 });
    const crownMaterial = new THREE.MeshStandardMaterial({ color: "#3f7b46", roughness: 1 });
    this.trunkMesh = new THREE.InstancedMesh(trunkGeometry, trunkMaterial, positions.length);
    this.crownMesh = new THREE.InstancedMesh(crownGeometry, crownMaterial, positions.length);
    const matrix = new THREE.Matrix4();
    for (let i = 0; i < positions.length; i += 1) {
      matrix.makeTranslation(positions[i]!.x, positions[i]!.y + 4, positions[i]!.z);
      this.trunkMesh.setMatrixAt(i, matrix);
      matrix.makeTranslation(positions[i]!.x, positions[i]!.y + 12, positions[i]!.z);
      this.crownMesh.setMatrixAt(i, matrix);
      this.crownMesh.setColorAt(i, colors[i]!.clone().offsetHSL(0, 0.06, -0.05));
    }
    this.trunkMesh.instanceMatrix.needsUpdate = true;
    this.crownMesh.instanceMatrix.needsUpdate = true;
    if (this.crownMesh.instanceColor) this.crownMesh.instanceColor.needsUpdate = true;
    this.treeRoot.add(this.trunkMesh, this.crownMesh);
  }

  private disposeTrees(): void {
    if (this.trunkMesh) {
      this.treeRoot.remove(this.trunkMesh);
      this.trunkMesh.geometry.dispose();
      (this.trunkMesh.material as THREE.Material).dispose();
    }
    if (this.crownMesh) {
      this.treeRoot.remove(this.crownMesh);
      this.crownMesh.geometry.dispose();
      (this.crownMesh.material as THREE.Material).dispose();
    }
    this.trunkMesh = null;
    this.crownMesh = null;
  }
}
